import type {
  PickerProps,
  RangePickerProps
} from 'antd/es/date-picker/generatePicker';
import generatePicker from 'antd/es/date-picker/generatePicker';
import type { Moment } from 'moment';
import momentGenerateConfig from 'rc-picker/lib/generate/moment';
import React, { forwardRef, useId } from 'react';
import styled, { css } from 'styled-components';
import useT from '@tools/ui-hooks/useT';
import { IconWrap } from '../../icons/icon';
import { theme } from '../../theme/semanticTokens';
import { IconSize } from '../../types/icons/Icon.types';
import { IconType } from '../../types/icons/IconTypes.enum';
import { inputFieldBorderStyle } from '../_shared/InputField';

const AntDatePicker = generatePicker<Moment>(momentGenerateConfig);

type SharedDatePickerFieldProps = Omit<
  Pick<
    PickerProps<Moment>,
    | 'disabled'
    | 'bordered'
    | 'picker'
    | 'format'
    | 'disabledDate'
    | 'renderExtraFooter'
    | 'open'
    | 'onOpenChange'
    | 'onFocus'
    | 'onBlur'
    | 'className'
    | 'id'
    | 'name'
    | 'aria-label'
    | 'aria-labelledby'
  >,
  'picker'
>;

type SingleDateProps = Partial<{ range: false }> &
  Pick<
    PickerProps<Moment>,
    'value' | 'defaultValue' | 'placeholder' | 'onChange'
  >;

export type DatePickerPreset = {
  label: string;
  value: [Moment, Moment];
};

type RangeDateProps = { range: true } & Pick<
  RangePickerProps<Moment>,
  'value' | 'defaultValue' | 'placeholder' | 'onChange'
>;

export type DatePickerProps = SharedDatePickerFieldProps &
  Partial<{
    picker: 'date' | 'month' | 'year';
    readOnly: boolean;
    allowClear: boolean;
    status: 'error' | 'default';
    presets: DatePickerPreset[];
  }> &
  (SingleDateProps | RangeDateProps);

const DatePickerWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const ForwardedAntDatePicker = forwardRef<
  React.ElementRef<typeof AntDatePicker>,
  any
>((props, ref) => <AntDatePicker {...props} ref={ref} />);

const ForwardedAntRangePicker = forwardRef<
  React.ElementRef<typeof AntDatePicker.RangePicker>,
  any
>((props, ref) => <AntDatePicker.RangePicker {...props} ref={ref} />);

const StyledDropdown = styled.div<{ $hasPresets?: boolean }>`
  .ant-picker-dropdown {
    .ant-picker-panel-container {
      background: ${theme.dime.colors.surface.primary};
      border-radius: ${theme.dime.radius.m};
      box-shadow: 0px 0px 7px rgba(0, 0, 0, 0.08);
      ${({ $hasPresets }) => $hasPresets && `display: flex;`}
    }

    .ant-picker-header {
      border-bottom: 1px solid ${theme.dime.colors.border.static};

      button {
        color: ${theme.dime.colors.icon.primary};
      }

      .ant-picker-header-view {
        line-height: ${theme.dime.fonts.lineHeights.md};
        color: ${theme.dime.colors.text.primary};
      }
    }

    .ant-picker-cell {
      padding: ${theme.dime.spacing.s} 0;
      color: ${theme.dime.colors.text.disabled};

      .ant-picker-cell-inner {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: ${theme.dime.radius.s};
        line-height: ${theme.dime.fonts.lineHeights.md};
      }

      &.ant-picker-cell-in-view {
        color: ${theme.dime.colors.text.primary};
      }

      &:not(.ant-picker-cell-disabled):hover .ant-picker-cell-inner {
        background: ${theme.dime.colors.surface.secondaryHover};
      }

      &.ant-picker-cell-today .ant-picker-cell-inner::before {
        border-color: ${theme.dime.colors.border.interactiveSelected};
      }

      &.ant-picker-cell-selected .ant-picker-cell-inner {
        background: ${theme.dime.colors.fill.brandPrimary};
        color: ${theme.dime.colors.text.inverse};
      }

      &.ant-picker-cell-disabled .ant-picker-cell-inner {
        color: ${theme.dime.colors.text.disabled};
      }
    }

    .ant-picker-footer {
      border-top: 1px solid ${theme.dime.colors.border.static};

      ${({ $hasPresets }) =>
        $hasPresets &&
        `
        order: -1;
        border-top: none;
        border-right: 1px solid ${theme.dime.colors.border.static};
        padding: ${theme.dime.spacing.l} 0;
      `}
    }

    ${({ $hasPresets }) =>
      $hasPresets &&
      `
      .ant-picker-ranges {
        display: flex;
        flex-direction: column;
        gap: ${theme.dime.spacing.s};
        margin: 0;
        padding: 0 ${theme.dime.spacing.l};
        min-width: 180px;
      }

      .ant-picker-preset {
        display: block;

        span {
          display: flex;
          align-items: center;
          height: ${theme.dime.spacing['3xl']};
          padding: 0 ${theme.dime.spacing.s};
          border-radius: ${theme.dime.radius.s};
          color: ${theme.dime.colors.text.primary};
          line-height: ${theme.dime.fonts.lineHeights.md};
          cursor: pointer;
          white-space: nowrap;

          &:hover {
            background: ${theme.dime.colors.surface.secondaryHover};
          }
        }
      }
    `}
  }
`;

type PickerFieldStyleProps = {
  $error: boolean;
  $disabled: boolean;
  $readOnly: boolean;
  $bordered: boolean;
};

const pickerFieldStyles = css<PickerFieldStyleProps>`
  &&.ant-picker {
    ${inputFieldBorderStyle}
    border-radius: ${theme.dime.radius.m};
    padding: 0
      ${({ $bordered }) =>
        $bordered ? theme.dime.spacing['2m'] : theme.dime.spacing.s};
    height: ${({ $bordered }) =>
      $bordered ? theme.dime.spacing['3xl'] : theme.dime.spacing['2xl']};
    gap: ${theme.dime.spacing.l};
    box-shadow: none;
    width: 100%;
    pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};

    ${({ $bordered }) =>
      !$bordered &&
      `
      border-color: transparent;
      background-color: transparent;

      &:hover,
      &:focus-within {
        border-color: transparent;
      }
    `}
  }

  &&.ant-picker-disabled input::placeholder {
    color: ${theme.dime.colors.text.disabled};
  }

  &&.ant-picker input {
    cursor: ${({ $disabled, $readOnly }) =>
      $disabled || $readOnly ? 'not-allowed' : 'text'};
    background-color: transparent;
    line-height: ${theme.dime.fonts.lineHeights.md};
  }

  &&.ant-picker:has(.ant-picker-clear):hover .ant-picker-suffix {
    visibility: hidden;
  }

  && .ant-picker-suffix {
    margin-inline-start: 0;
  }
`;

const StyledDatePicker = styled(ForwardedAntDatePicker)<PickerFieldStyleProps>`
  ${pickerFieldStyles}
`;

const StyledRangePicker = styled(
  ForwardedAntRangePicker
)<PickerFieldStyleProps>`
  ${pickerFieldStyles}

  &&.ant-picker-range .ant-picker-active-bar {
    display: none;
  }
`;

export const DatePicker = forwardRef<
  React.ElementRef<typeof AntDatePicker>,
  DatePickerProps
>(
  (
    {
      placeholder,
      value,
      defaultValue,
      onChange,
      range = false,
      presets,
      disabled = false,
      readOnly = false,
      allowClear = true,
      bordered = true,
      format = 'MM/DD/YYYY',
      open,
      className = '',
      id,
      'aria-labelledby': ariaLabelledBy,
      status = 'default',
      ...rest
    },
    ref
  ) => {
    const { t } = useT();
    const hasError = status === 'error';
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const hasPresets = range && !!presets?.length;
    const rangePresets = hasPresets
      ? Object.fromEntries(
          (presets as DatePickerPreset[]).map(
            ({ label, value: presetValue }) => [label, presetValue]
          )
        )
      : undefined;

    const sharedFieldProps = {
      ...rest,
      disabled,
      inputReadOnly: readOnly,
      open: readOnly ? false : open,
      format,
      id: inputId,
      'aria-labelledby': ariaLabelledBy,
      allowClear: (disabled || readOnly || !allowClear
        ? false
        : {
            clearIcon: (
              <IconWrap
                type={IconType.Close}
                size={IconSize.Small}
                style={{ color: theme.dime.colors.icon.primary }}
              />
            )
          }) as false | { clearIcon: React.ReactNode },
      getPopupContainer: (triggerNode: HTMLElement) =>
        triggerNode.parentElement ?? document.body,
      suffixIcon: (
        <IconWrap
          type={IconType.Calendar}
          size={IconSize.Small}
          disabled={disabled || readOnly}
          style={
            disabled || readOnly
              ? undefined
              : { color: theme.dime.colors.icon.primary }
          }
        />
      ),
      $error: hasError,
      $disabled: disabled,
      $readOnly: readOnly,
      $bordered: bordered
    };

    return (
      <DatePickerWrapper className={className}>
        <StyledDropdown $hasPresets={hasPresets}>
          {range ? (
            <StyledRangePicker
              {...sharedFieldProps}
              ref={
                ref as React.Ref<
                  React.ElementRef<typeof AntDatePicker.RangePicker>
                >
              }
              value={value as RangeDateProps['value']}
              defaultValue={defaultValue as RangeDateProps['defaultValue']}
              onChange={onChange as RangeDateProps['onChange']}
              placeholder={placeholder as RangeDateProps['placeholder']}
              ranges={rangePresets}
              separator={
                <IconWrap
                  type={IconType.ChevronRightEnd}
                  size={IconSize.Small}
                  disabled={disabled || readOnly}
                  style={
                    disabled || readOnly
                      ? undefined
                      : { color: theme.dime.colors.icon.primary }
                  }
                />
              }
            />
          ) : (
            <StyledDatePicker
              {...sharedFieldProps}
              ref={ref}
              value={value as SingleDateProps['value']}
              defaultValue={defaultValue as SingleDateProps['defaultValue']}
              onChange={onChange as SingleDateProps['onChange']}
              placeholder={
                (placeholder as SingleDateProps['placeholder']) ??
                t('datePicker.placeholder')
              }
            />
          )}
        </StyledDropdown>
      </DatePickerWrapper>
    );
  }
);

DatePicker.displayName = 'DatePicker';
